import { useEffect, useState } from "react";
import { Download, Smartphone, Bell } from "lucide-react";
import { toast } from "sonner";
import {
  enablePushNotifications,
  pushAskedBefore,
  pushPermission,
  pushSupported,
} from "@/lib/push";
import { track } from "@/lib/analytics";

/**
 * InstallAppButton — surfaces the PWA install prompt.
 *
 *  - On Chrome / Edge / Android: captures the `beforeinstallprompt` event and
 *    calls `prompt()` when the user clicks.
 *  - On iOS Safari (which doesn't fire beforeinstallprompt): falls back to a
 *    short "Add to Home Screen" instruction modal.
 *  - Hides itself entirely once the app is already installed / running in
 *    standalone mode, or after a successful install.
 *  - On successful install, automatically chains into a push-notification
 *    opt-in so fans get pinged for new articles and Titan Up tracker updates.
 */
async function maybeOptInToPush() {
  if (!pushSupported()) return;
  if (pushPermission() !== "default") return; // already granted/denied
  if (pushAskedBefore()) return;
  try {
    const sub = await enablePushNotifications();
    if (sub) {
      track("push_optin");
      toast.success("Notifications enabled — you'll be the first to know.");
    }
  } catch {
    /* user declined or error — silent */
  }
}

/**
 * InstallAppButton — surfaces the PWA install prompt.
 *
 *  - On Chrome / Edge / Android: captures the `beforeinstallprompt` event and
 *    calls `prompt()` when the user clicks.
 *  - On iOS Safari (which doesn't fire beforeinstallprompt): falls back to a
 *    short "Add to Home Screen" instruction modal.
 *  - Hides itself entirely once the app is already installed / running in
 *    standalone mode, or after a successful install.
 */
export default function InstallAppButton({ variant = "compact" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    // Already installed / running as PWA?
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) {
      setInstalled(true);
      // First launch after install (e.g. iOS Add-to-Home-Screen) — opt in to push.
      maybeOptInToPush();
      return undefined;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      track("app_installed");
      // Chain the push opt-in right after the install completes.
      maybeOptInToPush();
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleClick = async () => {
    // Chromium / Android path
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") setInstalled(true);
      } catch {
        /* user dismissed */
      }
      setDeferredPrompt(null);
      return;
    }
    // iOS Safari path — no programmatic prompt available, show instructions.
    const isIos =
      /iPad|iPhone|iPod/i.test(navigator.userAgent) && !window.MSStream;
    if (isIos) {
      setShowIosHint(true);
      return;
    }
    // Any other browser where the install API isn't supported:
    setShowIosHint(true);
  };

  if (installed) return null;

  const baseClasses =
    "inline-flex items-center gap-2 font-cond uppercase tracking-wider transition-colors";

  if (variant === "hero") {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          data-testid="install-app-hero-btn"
          className={`${baseClasses} btn-ghost text-[11px] px-4 py-2.5`}
        >
          <Download size={13} />
          Install the App
        </button>
        {showIosHint && <IosInstallHint onClose={() => setShowIosHint(false)} />}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="install-app-btn"
        title="Install Titan Standard"
        className={`${baseClasses} border border-titans-blue/60 text-titans-blue hover:bg-titans-blue hover:text-white px-3 py-1.5 text-xs`}
      >
        <Smartphone size={12} />
        <span className="hidden md:inline">Install App</span>
      </button>
      {showIosHint && <IosInstallHint onClose={() => setShowIosHint(false)} />}
    </>
  );
}

function IosInstallHint({ onClose }) {
  const isIos =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/i.test(navigator.userAgent);
  return (
    <div
      data-testid="install-hint-modal"
      className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-5"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      role="button"
      tabIndex={0}
    >
      <div
        className="titans-card max-w-md w-full p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue mb-2">
          Install the App
        </div>
        <h3 className="font-display text-2xl uppercase tracking-wide section-title mb-4">
          Add to Home Screen
        </h3>
        {isIos ? (
          <ol className="space-y-3 text-titans-silver font-body text-sm leading-relaxed">
            <li>
              <span className="font-cond uppercase tracking-wider text-white">1.</span>{" "}
              Tap the <strong className="text-white">Share</strong> icon at the bottom of Safari.
            </li>
            <li>
              <span className="font-cond uppercase tracking-wider text-white">2.</span>{" "}
              Scroll down and tap <strong className="text-white">Add to Home Screen</strong>.
            </li>
            <li>
              <span className="font-cond uppercase tracking-wider text-white">3.</span>{" "}
              Tap <strong className="text-white">Add</strong> in the top-right corner.
            </li>
            <li>
              <span className="font-cond uppercase tracking-wider text-white">4.</span>{" "}
              Open <strong className="text-white">Titan Standard</strong> from your home screen.
            </li>
          </ol>
        ) : (
          <p className="text-titans-silver font-body text-sm leading-relaxed">
            Your browser doesn&apos;t support one-click install. On most browsers, look for an{" "}
            <strong className="text-white">Install</strong> icon in the address bar, or open the
            browser menu and choose <strong className="text-white">Install Titan Standard</strong>.
          </p>
        )}
        <button
          type="button"
          onClick={onClose}
          data-testid="install-hint-close"
          className="mt-6 w-full bg-titans-red hover:bg-titans-red-hover text-white font-cond uppercase tracking-wider text-sm py-3"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
